import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Alert from "@mui/material/Alert";
import ButtonBase from "@mui/material/ButtonBase";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { DateTime } from "luxon";

import { dateUtils } from "/imports/libs/url";
import { unavailableReasonProps } from "/imports/libs/unavailableReason";
import { DateSelector, EmptyState, LoadingSpinner, PickerRulesPanel, ServiceAvailabilityNotice } from "/imports/ui/components/shared";
import {
  TimeSlotCard,
  SLOT_SECTION_LABEL_SX,
  CARD_SX,
  getUniformGridPrice,
  PackageOfferBanner,
  PackComposerTray,
  PackHorizonBanner,
  PackContinuationBanner
} from "/imports/ui/components/booking";
import { pickerContinuationCopy } from "/imports/libs/packContinuation";
import DurationPrompt from "./components/DurationPrompt";
import LessonFilterSidebar from "./components/LessonFilterSidebar";
import LessonWeekGrid from "./components/LessonWeekGrid";
import LessonOptionsPopover from "./components/LessonOptionsPopover";
import LessonRescheduleBanner from "./components/LessonRescheduleBanner";
import LessonRescheduleDialog from "./components/LessonRescheduleDialog";
import LessonRescheduleTray from "./components/LessonRescheduleTray";
import { rescheduleEmptyProps, packWindowEmptyProps } from "./lessonHelpers";

// Desktop layout — sticky filter sidebar + a results pane that toggles between
// Day (one day's bucketed slot cards) and Week (a 7-day grid: days as columns,
// time-of-day buckets as rows). Multi-option times open a popover in both modes.
function LessonsDesktopLayout({ lesson }) {
  const {
    navigate,
    slug,
    currency,
    filters,
    setFilter,
    setSelectedSlot,
    loading,
    error,
    availability,
    viewMode,
    setViewMode,
    weekStart,
    weekDates,
    weekGrouped,
    maxBookableDate,
    canGoNextWeek,
    suggestedGroups,
    bucketedSlots,
    handleWeekChipClick,
    getOptionsLabel,
    goPrevWeek,
    goNextWeek,
    packageOffer,
    handleViewPackage
  } = lesson;

  const weekHasAnySlots = weekDates.some((d) => (weekGrouped.get(d) || []).length > 0);
  // A dated closure / time off emptied the day: say so, and offer the lever
  // the client actually has (drop the resource filter) rather than the date.
  const closureProps = unavailableReasonProps({
    closureReasons: availability?.closureReasons,
    canClearResource: !!filters.trainerId && lesson.allowTrainerPick !== false,
    onClearResource: () => {
      setFilter("trainerId", null);
      setSelectedSlot(null);
    },
    onNextDate: () => {
      setFilter("date", dateUtils.toUrlDate(new Date(Date.now() + 86400000)));
      setSelectedSlot(null);
    }
  });

  const availabilityInfo = availability?.availabilityInfo || [];
  const weekLabel = `${DateTime.fromISO(weekDates[0]).toFormat("MMM d")} – ${DateTime.fromISO(weekDates[6]).toFormat("MMM d, yyyy")}`;
  const canGoPrevWeek = weekStart > dateUtils.weekStart(new Date());

  // One shared price across the whole day → drop it from every cell (the
  // duration picker already states it); varying prices stay per-cell.
  const uniformDayPrice = getUniformGridPrice(bucketedSlots.flatMap((b) => b.groups));

  const packMode = lesson.pack?.active;
  const continuationCopy = packMode ? pickerContinuationCopy(lesson.packContinuation, lesson.pack.opt?.linkedCategoryType) : null;
  const isRescheduling = lesson.reschedule?.active;
  // Moving a lesson: a day (or week) outside the package's dates is explained before anything else.
  const packWindowEmpty = isRescheduling ? packWindowEmptyProps(lesson.reschedule.info, { from: filters.date, onJumpTo: lesson.jumpToDate }) : null;
  const packWindowWeekEmpty = isRescheduling
    ? packWindowEmptyProps(lesson.reschedule.info, { from: weekDates[0], to: weekDates[6], week: true, onJumpTo: lesson.jumpToDate })
    : null;
  // The tray of the client's own booked lessons sits at the bottom, like the pack tray.
  const hasOwnTray = isRescheduling && (lesson.reschedule.info?.ownBookings?.length || 0) > 0;

  const renderDaySlotCard = (group) => {
    const isMulti = group.slots.length > 1;
    return (
      <TimeSlotCard
        key={group.time}
        time={group.time}
        price={group.minPrice}
        currency={group.currency || currency}
        selected={packMode && lesson.pack.isPicked(filters.date, group.time)}
        onClick={(e) => handleWeekChipClick(e, group, filters.date)}
        optionsLabel={getOptionsLabel(group.slots)}
        pricePrefix={isMulti ? "from" : null}
        hidePrice={packMode || uniformDayPrice != null}
        dense
        align="left"
      />
    );
  };

  return (
    <Box sx={{ pb: packMode || hasOwnTray ? { xs: "11rem", md: "7rem" } : 0 }}>
      {/* Title */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
        <IconButton size="small" onClick={lesson.goBack} aria-label="Go back" sx={{ ml: -0.5 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography sx={{ fontSize: "1.75rem", fontWeight: 800, letterSpacing: "-0.02em" }}>
          {packMode ? "Pick your pack sessions" : isRescheduling ? "Move your lesson" : "Book a Private Lesson"}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {packMode
          ? "Tap the times you want — the week view shows the whole pattern at once. Your picks stay as you move between days."
          : isRescheduling
            ? "Pick the new time. Same lesson, same length."
            : "Set your filters, then pick a time."}
      </Typography>

      {isRescheduling && <LessonRescheduleBanner reschedule={lesson.reschedule} currency={currency} sx={{ mb: 3, maxWidth: "48rem" }} />}

      {/* The club's rules for buying a pack, in the one place the client can still
          act on them. Only while composing a pack — an ordinary booking has no
          pack rules to obey. */}
      {packMode && <PickerRulesPanel title="Before you buy" rules={lesson.pack.packRules} sx={{ mb: 3 }} />}

      {packMode && continuationCopy && (
        <PackContinuationBanner {...continuationCopy} onAction={lesson.goToPackContinuation} sx={{ mb: 3, maxWidth: "42rem" }} />
      )}

      {/* A pack outlives the club's booking window, and the lessons it covers
          stay bookable for as long as it does. Never while BUYING a pack: the
          client is composing sessions for a different one, and credit they
          already hold has nothing to say about those. */}
      {!packMode && !isRescheduling && (
        <PackHorizonBanner
          horizon={lesson.ownedPackHorizon}
          start={lesson.ownedPackStart}
          count={lesson.ownedPackCount}
          capped={lesson.ownedPackCapped}
          noun="lessons"
          onJump={lesson.goToPackStart}
          sx={{ mb: 3, maxWidth: "42rem" }}
        />
      )}

      <Box sx={{ display: "flex", gap: 4, alignItems: "flex-start" }}>
        <LessonFilterSidebar lesson={lesson} />

        {/* Week pane */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Sticky header: view toggle + (day date strip / week navigation). */}
          <Box sx={{ position: "sticky", top: 64, zIndex: 4, bgcolor: "background.default", pt: 0.5, pb: 1.5 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: viewMode === "day" ? 2 : 0,
                gap: 2,
                flexWrap: "wrap"
              }}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  p: 0.4,
                  borderRadius: "9999px",
                  border: "1.5px solid",
                  borderColor: "divider",
                  bgcolor: "#fff",
                  gap: 0.4
                }}
              >
                {["day", "week"].map((m) => {
                  const active = viewMode === m;
                  return (
                    <ButtonBase
                      key={m}
                      onClick={() => setViewMode(m)}
                      sx={{ px: 2, py: 0.55, borderRadius: "9999px", bgcolor: active ? "primary.main" : "transparent", transition: "all 0.15s ease" }}
                    >
                      <Typography
                        sx={{ fontSize: "0.8rem", fontWeight: 600, textTransform: "capitalize", color: active ? "#fff" : "text.secondary" }}
                      >
                        {m}
                      </Typography>
                    </ButtonBase>
                  );
                })}
              </Box>

              {viewMode === "week" && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Typography sx={{ fontSize: "1rem", fontWeight: 700, letterSpacing: "-0.01em" }}>{weekLabel}</Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={goPrevWeek}
                      disabled={!canGoPrevWeek}
                      sx={{ border: "1px solid", borderColor: "divider", borderRadius: "50%", width: "2.125rem", height: "2.125rem" }}
                    >
                      <ChevronLeftIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={goNextWeek}
                      disabled={!canGoNextWeek}
                      sx={{ border: "1px solid", borderColor: "divider", borderRadius: "50%", width: "2.125rem", height: "2.125rem" }}
                    >
                      <ChevronRightIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              )}
            </Box>

            {viewMode === "day" && (
              <Paper elevation={0} sx={{ ...CARD_SX, p: 2 }}>
                <DateSelector
                  value={filters.date}
                  onChange={(date) => {
                    setFilter("date", date);
                    setSelectedSlot(null);
                  }}
                  daysToShow={7}
                  minDate={lesson.minBookableDate}
                  hideBeforeMin
                  maxDate={maxBookableDate}
                  revealKey={lesson.dateRevealKey}
                  dense
                />
              </Paper>
            )}
          </Box>

          {/* Directly under the day strip, across the results pane: the client
              is already looking here to choose a day, and the sidebar's own
              column is too quiet a place for an offer to be noticed. Held back
              while a search is in flight or has failed — the times it prices
              against are gone from the screen, and the offer left standing
              would be the previous search's. */}
          {!loading && !error && <PackageOfferBanner offer={packageOffer} currency={currency} onClick={handleViewPackage} sx={{ mb: 2.5 }} />}

          {!filters.duration ? (
            <DurationPrompt />
          ) : viewMode === "day" ? (
            <>
              {loading ? (
                <LoadingSpinner message="Loading availability..." />
              ) : error ? (
                <Alert severity="error">{error}</Alert>
              ) : bucketedSlots.length > 0 ? (
                <>
                  {suggestedGroups.length > 0 && (
                    <Box sx={{ mb: 3.5 }}>
                      <Typography sx={{ ...SLOT_SECTION_LABEL_SX, mb: 1.5 }}>Suggested for you · {suggestedGroups.length}</Typography>
                      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(7.75rem, 1fr))", gap: 1.25 }}>
                        {suggestedGroups.map(renderDaySlotCard)}
                      </Box>
                    </Box>
                  )}
                  {bucketedSlots.map((bucket) => (
                    <Box key={bucket.id} sx={{ mb: 3.5 }}>
                      <Typography sx={{ ...SLOT_SECTION_LABEL_SX, mb: 1.5 }}>
                        {bucket.label} · {bucket.groups.length}
                      </Typography>
                      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(7.75rem, 1fr))", gap: 1.25 }}>
                        {bucket.groups.map(renderDaySlotCard)}
                      </Box>
                    </Box>
                  ))}
                </>
              ) : packWindowEmpty ? (
                <EmptyState {...packWindowEmpty} />
              ) : closureProps ? (
                <EmptyState {...closureProps} />
              ) : availabilityInfo.length > 0 ? (
                <ServiceAvailabilityNotice
                  availabilityInfo={availabilityInfo}
                  actionLabel="Try Tomorrow"
                  onAction={() => {
                    setFilter("date", dateUtils.toUrlDate(new Date(Date.now() + 86400000)));
                    setSelectedSlot(null);
                  }}
                />
              ) : isRescheduling ? (
                <EmptyState
                  {...rescheduleEmptyProps(lesson.reschedule.info, {
                    onNext: () => {
                      setFilter("date", dateUtils.toUrlDate(new Date(Date.now() + 86400000)));
                      setSelectedSlot(null);
                    }
                  })}
                />
              ) : (
                <EmptyState
                  title="No availability"
                  description="No trainers are available for the selected date and filters. Try a different date or adjust your filters."
                  actionLabel="Try Tomorrow"
                  onAction={() => {
                    setFilter("date", dateUtils.toUrlDate(new Date(Date.now() + 86400000)));
                    setSelectedSlot(null);
                  }}
                />
              )}
            </>
          ) : loading ? (
            <LoadingSpinner message="Loading availability..." />
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : !weekHasAnySlots ? (
            packWindowWeekEmpty ? (
              <EmptyState {...packWindowWeekEmpty} />
            ) : availabilityInfo.length > 0 ? (
              <ServiceAvailabilityNotice availabilityInfo={availabilityInfo} actionLabel="Next week" onAction={goNextWeek} />
            ) : isRescheduling ? (
              <EmptyState {...rescheduleEmptyProps(lesson.reschedule.info, { week: true, onNext: goNextWeek })} />
            ) : (
              <EmptyState
                title="No availability this week"
                description="No trainers are available for the selected filters this week. Try the next week or adjust your filters."
                actionLabel="Next week"
                onAction={goNextWeek}
              />
            )
          ) : (
            <LessonWeekGrid lesson={lesson} />
          )}
        </Box>
      </Box>

      <LessonOptionsPopover lesson={lesson} />
      <PackComposerTray pack={lesson.pack} onGoToDate={lesson.jumpToDate} />
      {isRescheduling && <LessonRescheduleTray reschedule={lesson.reschedule} />}
      {isRescheduling && <LessonRescheduleDialog reschedule={lesson.reschedule} currency={currency} isMobile={false} />}
    </Box>
  );
}

export default LessonsDesktopLayout;
